import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { RefreshCw } from 'lucide-react'
import { useTasks } from '@/hooks/useTasks'
import { Button } from '@/components/ui/button'
import { StatusBadge } from '@/components/ui/status-badge'
import { LoadingSpinner } from '@/components/ui/loading-spinner'
import { TASK_STATUS } from '@/lib/constants'
import { workerRoutes } from '@/lib/navigation/routes'
import { formatDateTime } from '@/lib/utils'
import type { TaskCreationMode, TaskStatus, TaskSummary } from '@/types'

const CREATION_MODE_LABEL: Record<TaskCreationMode, string> = {
  km_and_ka_file: 'КМ + КА из файлов',
  ka_file_only: 'Только КА из файлов',
}

function modeBadge(task: TaskSummary) {
  if (task.creation_mode === 'km_and_ka_file') return null
  const label = task.creation_mode ? CREATION_MODE_LABEL[task.creation_mode] : 'Другой режим'
  return (
    <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
      {label ?? 'скоро'}
    </span>
  )
}

export function WorkerTaskListPage() {
  const navigate = useNavigate()
  const { data: tasks, isLoading, refetch, isFetching } = useTasks()
  const [selectedStatuses, setSelectedStatuses] = useState<Set<TaskStatus>>(new Set())

  const toggleStatus = (status: TaskStatus) => {
    setSelectedStatuses(prev => {
      const next = new Set(prev)
      if (next.has(status)) next.delete(status)
      else next.add(status)
      return next
    })
  }

  const visibleTasks = tasks
    ? selectedStatuses.size > 0
      ? tasks.filter(t => selectedStatuses.has(t.status as TaskStatus))
      : tasks
    : []

  if (isLoading) return <LoadingSpinner className="mt-20" />

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Задания</h1>
          <p className="text-sm text-gray-500 mt-1">Выберите задание для работы на посту</p>
        </div>
        <Button variant="ghost" size="sm" onClick={() => refetch()} isLoading={isFetching}>
          <RefreshCw className="w-4 h-4" />
        </Button>
      </div>

      <div className="flex gap-2 flex-wrap mb-4 items-center">
        <span className="text-xs text-gray-500 font-medium mr-1">Фильтр:</span>
        {(Object.entries(TASK_STATUS) as [TaskStatus, typeof TASK_STATUS[TaskStatus]][]).map(([status, meta]) => {
          const selected = selectedStatuses.has(status)
          const count = tasks?.filter(t => t.status === status).length ?? 0
          return (
            <button
              key={status}
              type="button"
              onClick={() => toggleStatus(status)}
              className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                selected
                  ? `${meta.color} ${meta.textColor} border-current ring-1 ring-current`
                  : 'bg-white text-gray-600 border-gray-200 hover:border-blue-400'
              }`}
            >
              {meta.label}
              <span className={`ml-1 ${selected ? 'opacity-80' : 'opacity-50'}`}>({count})</span>
            </button>
          )
        })}
        {selectedStatuses.size > 0 && (
          <button
            type="button"
            onClick={() => setSelectedStatuses(new Set())}
            className="px-3 py-1 rounded-full text-xs font-medium border bg-white text-gray-400 border-gray-200 hover:text-red-500 hover:border-red-300 transition-colors"
          >
            × Сбросить
          </button>
        )}
      </div>

      {!visibleTasks.length ? (
        <div className="text-center text-gray-500 py-20">
          {selectedStatuses.size > 0 ? (
            <p className="text-lg">Нет заданий с выбранными статусами</p>
          ) : (
            <p className="text-lg">Нет заданий</p>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              <tr>
                <th className="px-4 py-3">ID</th>
                <th className="px-4 py-3">Название</th>
                <th className="px-4 py-3">Статус</th>
                <th className="px-4 py-3">Коробки</th>
                <th className="px-4 py-3">Паллеты</th>
                <th className="px-4 py-3">Создано</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {visibleTasks.map(t => {
                const meta = TASK_STATUS[t.status]
                return (
                  <tr
                    key={t.id}
                    className="hover:bg-gray-50 transition-colors cursor-pointer"
                    onClick={() => navigate(workerRoutes.taskDetail(t.id))}
                  >
                    <td className="px-4 py-3 font-mono text-xs">{t.id}</td>
                    <td className="px-4 py-3 font-medium">
                      {t.name || `Задание ${t.id}`}
                      {modeBadge(t)}
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge color={meta.color} textColor={meta.textColor}>{meta.label}</StatusBadge>
                    </td>
                    <td className="px-4 py-3 text-center">{t.boxes_count}</td>
                    <td className="px-4 py-3 text-center">{t.pallets_count}</td>
                    <td className="px-4 py-3 text-gray-500">{formatDateTime(t.created_at)}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
